// src/context/ConversationContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as conversationApi from '../services/conversationApi';

const ConversationContext = createContext(null);

export const useConversations = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversations must be used within a ConversationProvider');
  }
  return context;
};

export const ConversationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  
  // State for managing conversations
  const [conversations, setConversations] = useState({});
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create or get conversation for an agent
  const getOrCreateConversation = async (agentId, agentName) => {
    const conversationKey = `${agentId}-${currentUser?.username || 'anonymous'}`;
    
    if (conversations[conversationKey]) {
      setActiveSessionId(conversations[conversationKey].sessionId);
      return conversations[conversationKey];
    }

    // Create new conversation structure
    const newConversation = {
      agentId,
      agentName,
      sessionId: null, // Will be set when first message is sent
      messages: [],
      createdAt: new Date(),
      lastActivity: new Date()
    };

    setConversations(prev => ({
      ...prev,
      [conversationKey]: newConversation
    }));

    return newConversation;
  };

  // Send message and update conversation
  const sendMessage = async (agentId, message, sessionId = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await conversationApi.sendMessage(
        message,
        sessionId,
        currentUser?.username
      );

      if (response.success) {
        const conversationKey = `${agentId}-${currentUser?.username || 'anonymous'}`;
        
        // Update conversation with new messages
        setConversations(prev => {
          const conversation = prev[conversationKey] || {};
          const newSessionId = response.sessionId || conversation.sessionId;
          
          const userMessage = {
            id: Date.now(),
            content: message,
            role: 'user',
            timestamp: new Date()
          };

          const assistantMessage = {
            id: response.responseMessageId || Date.now() + 1,
            content: response.response,
            role: 'assistant',
            timestamp: new Date(response.responseTimestamp || new Date()),
            intent: response.intent,
            ticketId: response.ticketId
          };

          return {
            ...prev,
            [conversationKey]: {
              ...conversation,
              sessionId: newSessionId,
              messages: [...(conversation.messages || []), userMessage, assistantMessage],
              lastActivity: new Date()
            }
          };
        });

        // Update active session
        if (response.sessionId) {
          setActiveSessionId(response.sessionId);
        }

        // If a task was created, refresh tasks
        if (response.ticketId) {
          await refreshTasks(response.sessionId);
        }

        return response;
      } else {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load conversation history
  const loadConversationHistory = async (sessionId) => {
    try {
      setLoading(true);
      const response = await conversationApi.getConversationHistory(sessionId);
      
      if (response.success && response.messages) {
        return response.messages.map(msg => ({
          id: msg.messageId,
          content: msg.content,
          role: msg.role,
          timestamp: new Date(msg.timestamp)
        }));
      }
      return [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Refresh tasks for a session
  const refreshTasks = async (sessionId = null) => {
    try {
      const response = await conversationApi.getUserTasks(sessionId);
      if (response.success && response.tasks) {
        setTasks(response.tasks);
      }
    } catch (err) {
      console.error('Error refreshing tasks:', err);
    }
  };

  // Execute a task
  const executeTask = async (ticketId) => {
    try {
      setLoading(true);
      const response = await conversationApi.executeTask(ticketId);
      
      if (response.success) {
        // Refresh tasks to update status
        await refreshTasks(activeSessionId);
        return response;
      } else {
        throw new Error(response.error || 'Failed to execute task');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get task status
  const getTaskStatus = async (ticketId) => {
    try {
      const response = await conversationApi.getTaskStatus(ticketId);
      return response;
    } catch (err) {
      console.error('Error getting task status:', err);
      throw err;
    }
  };

  // Clear error
  const clearError = () => setError(null);

  // Get conversation by agent ID
  const getConversation = (agentId) => {
    const conversationKey = `${agentId}-${currentUser?.username || 'anonymous'}`;
    return conversations[conversationKey] || null;
  };

  // Get all conversations for current user
  const getAllConversations = () => {
    const userPrefix = `${currentUser?.username || 'anonymous'}`;
    return Object.entries(conversations)
      .filter(([key]) => key.endsWith(userPrefix))
      .map(([key, conversation]) => ({ key, ...conversation }));
  };

  const value = {
    conversations,
    activeSessionId,
    tasks,
    loading,
    error,
    getOrCreateConversation,
    sendMessage,
    loadConversationHistory,
    refreshTasks,
    executeTask,
    getTaskStatus,
    clearError,
    getConversation,
    getAllConversations,
    setActiveSessionId
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};
